interface IInitialViewport {
  latitude: number
  longitude: number
  bearing: number
  pitch: number
  zoom: number
}

interface IViewportTrackerOptions {
  getMapRef: () => { getMap(): any } | null
  getInitialViewport: () => IInitialViewport | null
  onUnsavedChange: () => void
  // A recovery swaps the map instance (STX1-1068), so the listener follows it.
  onMapChange: (callback: (map: any) => void) => () => void
}

/**
 * Tracks user-initiated viewport changes on a Mapbox map.
 *
 * Attaches a `moveend` listener filtered by `originalEvent` so only
 * user-driven changes (drag, scroll-zoom, rotate, pitch) trigger
 * `onUnsavedChange`; programmatic changes (flyTo, GeolocateControl
 * trigger) are ignored.
 *
 * Returns a `stop` function that removes the listener and unsubscribes.
 */
export function startViewportTracking(
  options: IViewportTrackerOptions,
): () => void {
  let disposer: (() => void) | null = null
  const { getMapRef, getInitialViewport, onUnsavedChange } = options

  function attachMoveEndListener(map: any): void {
    const onMoveEnd = (e: { originalEvent?: Event }) => {
      if (!e.originalEvent) return
      const initial = getInitialViewport()
      if (!initial) return
      const { lat, lng } = map.getCenter()
      if (
        lat !== initial.latitude ||
        lng !== initial.longitude ||
        map.getBearing() !== initial.bearing ||
        map.getPitch() !== initial.pitch ||
        map.getZoom() !== initial.zoom
      ) {
        onUnsavedChange()
      }
    }
    map.on('moveend', onMoveEnd)
    disposer = () => map.off('moveend', onMoveEnd)
  }

  const rebind = (map: any): void => {
    if (disposer) {
      disposer()
      disposer = null
    }
    if (map) attachMoveEndListener(map)
  }

  const disposeMapWatch = options.onMapChange(rebind)
  rebind(getMapRef()?.getMap() ?? null)

  return () => {
    disposeMapWatch()
    if (disposer) {
      disposer()
      disposer = null
    }
  }
}
